import { Task } from '@lit/task';
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';

export abstract class DERElement extends LitElement {
    @property({ type: String }) public override id: string = '';
    @property({ type: Boolean }) protected isSmallDevice: boolean = false;

    static define<T extends CustomElementConstructor>(
        this: T,
        name: string,
        elementConstructor: T = this,
        options: ElementDefinitionOptions = {}
    ): boolean {
        const elementAlreadyExists: CustomElementConstructor | undefined = customElements.get(name);

        if (!elementAlreadyExists) {
            customElements.define(name, elementConstructor, options);
            return true;
        }

        console.warn(`Attempted to register <${name}>, but <${name}> has already been registered.`);
        return false;
    }

    override connectedCallback(): void {
        super.connectedCallback();
    }

    override disconnectedCallback(): void {
        window.removeEventListener('resize', this._handleResize);
        super.disconnectedCallback();
    }

    protected emitEvent(name: string, detail: unknown): void {
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    protected smallDeviceTask: Task<[boolean], boolean> = new Task(this, {
        task: async (): Promise<boolean> => this.isSmallDevice,
        args: () => [this.isSmallDevice],
    });

    protected checkMobile: () => void = () => {
        this._setIsMobile();
        window.addEventListener('resize', this._handleResize);
    };

    private _handleResize: () => void = () => {
        this._setIsMobile();
    };

    private _setIsMobile: () => void = () => {
        const width: number =
            this.shadowRoot?.host?.parentElement?.clientWidth ||
            window?.innerWidth ||
            self?.innerWidth ||
            top?.innerWidth ||
            0;
        this.isSmallDevice = width < 768;
    };

    protected warn(name: string, data: object): void {
        const detail: { name: string; data: object } = { name: name.toLowerCase(), data };

        this.dispatchEvent(new CustomEvent('der.warning', { detail, bubbles: true, composed: true }));
    }

    error(name: string, data: object): void {
        const detail: { name: string; data: object } = { name: name.toLowerCase(), data };

        this.dispatchEvent(new CustomEvent('der.error', { detail, bubbles: true, composed: true }));
    }
}
